import { TransactionInterface } from '@/interfaces/TransactionInterface';
import reactQueryClient from '@/services/ReactQueryClientService';
import { fetchTransactions } from '@/services/Transactions';
import { logError, logInfo } from '@/utils/logger';
import {
  QueryClientProvider,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useAuth } from '../preauth/AuthProvider';
import { useAuthorization } from './AuthorizationProvider';
import { useWalletsContext } from './UserWalletsProvider';
interface TransactionsContextType {
  isLoading: boolean;
  isInitialLoading: boolean;
  isForceFetchInProgress: boolean;
  error: Error | null;
  isUserAuthorized: boolean;
  transactionsFound: boolean;
  transactions: TransactionInterface[];
  cursor: string;
  refreshTransactions: () => Promise<void>;
}
const TransactionsContext = createContext<TransactionsContextType>(
  {} as TransactionsContextType,
);
interface TransactionsProviderProps {
  children: ReactNode;
  staleTime?: number;
  retry?: number;
}
const PROVIDER_NAME = 'UserTransactionsProvider';
let userTransactionsProviderInstance: any = null;
export const setTransactionsForceFetchInProgress = (state: boolean): void => {
  if (userTransactionsProviderInstance) {
    userTransactionsProviderInstance._setForceFetchInProgress(state);
  }
};
const QUERY_KEY_TRANSACTIONS = 'transactions';
export const useTransactions = () => useContext(TransactionsContext);
export const UserTransactionsProvider = ({
  children,
  staleTime = 60 * 60 * 1000,
  retry = 3,
}: TransactionsProviderProps): JSX.Element => {
  const [isForceFetchInProgress, setIsForceFetchInProgress] = useState(false);
  const _setForceFetchInProgress = useCallback((state: boolean) => {
    setIsForceFetchInProgress(state);
  }, []);
  useEffect(() => {
    userTransactionsProviderInstance = { _setForceFetchInProgress };
    return () => {
      userTransactionsProviderInstance = null;
    };
  }, [_setForceFetchInProgress]);
  const { user } = useAuth();
  const { isUserAuthorized, setUnauthorized } = useAuthorization();
  const queryClient = useQueryClient();
  const { isLoading: walletsLoading, wallets } = useWalletsContext();
  const [isManualRefreshTriggered, setIsManualRefreshTriggered] =
    useState(false);
  const [isInitialLoadAttempted, setIsInitialLoadAttempted] = useState(false);
  const handleUnauthorized = useCallback(() => {
    setUnauthorized();
    queryClient.removeQueries({ queryKey: [QUERY_KEY_TRANSACTIONS] });
    logError(
      'User is unauthorized. Disabling further transactions fetch attempts.',
      PROVIDER_NAME,
    );
  }, [queryClient, setUnauthorized]);
  const {
    data: transactionsData,
    error,
    isLoading: isQueryLoading,
    isFetching: isQueryFetching,
    refetch: refetchTransactionsQuery,
  } = useQuery({
    queryKey: [QUERY_KEY_TRANSACTIONS],
    queryFn: fetchTransactions,
    enabled:
      !!user && isUserAuthorized && !walletsLoading && wallets.length > 0,
    staleTime,
    refetchOnWindowFocus: false,
    retry: (failureCount, error: any) => {
      if (error?.response?.status === 401) {
        handleUnauthorized();
        return false;
      }
      return failureCount < retry;
    },
    placeholderData: (previousData) => previousData,
  });
  useEffect(() => {
    if (transactionsData && !isInitialLoadAttempted) {
      logInfo('Initial transactions loaded successfully.', PROVIDER_NAME);
      setIsInitialLoadAttempted(true);
    }
  }, [transactionsData, isInitialLoadAttempted]);
  useEffect(() => {
    if (!walletsLoading && wallets.length > 0 && isUserAuthorized && !!user) {
      logInfo(
        'Wallets loaded successfully, now fetching transactions',
        PROVIDER_NAME,
      );
    }
  }, [walletsLoading, wallets.length, isUserAuthorized, user]);
  useEffect(() => {
    if (error && !isInitialLoadAttempted) {
      logInfo('Initial transaction load resulted in an error.', PROVIDER_NAME);
      setIsInitialLoadAttempted(true);
    }
  }, [error, isInitialLoadAttempted]);
  const isInitialLoading = !isInitialLoadAttempted && isQueryLoading;
  const isManualLoading = isManualRefreshTriggered && isQueryFetching;
  const isLoading =
    isInitialLoading || isManualLoading || isForceFetchInProgress;
  useEffect(() => {
    if (isForceFetchInProgress) {
      logInfo('Force fetch in progress for transactions', PROVIDER_NAME);
    }
  }, [isForceFetchInProgress]);
  const effectiveInitialLoading = isInitialLoading || isForceFetchInProgress;
  const transactions: TransactionInterface[] =
    transactionsData?.transactions || [];
  const cursor: string = transactionsData?.cursor || '';
  const transactionsFound = useMemo(
    () => transactions.length > 0,
    [transactions],
  );
  const refreshTransactions = useCallback(async (): Promise<void> => {
    const caller = `${PROVIDER_NAME}.refreshTransactions`;
    if (isManualRefreshTriggered || isQueryFetching) {
      logInfo('Refresh already in progress, skipping.', caller);
      return;
    }
    setIsManualRefreshTriggered(true);
    logInfo('Starting manual refresh of transactions.', caller);
    try {
      logInfo('Invalidating transactions query.', caller);
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY_TRANSACTIONS] });
      logInfo('Refetching transactions.', caller);
      await refetchTransactionsQuery();
      logInfo('Transactions refreshed successfully.', caller);
    } catch (err) {
      logError(err, caller);
      throw err;
    } finally {
      setIsManualRefreshTriggered(false);
    }
  }, [
    refetchTransactionsQuery,
    isManualRefreshTriggered,
    isQueryFetching,
    queryClient,
  ]);
  const contextValue = useMemo(
    () => ({
      isLoading,
      isInitialLoading: effectiveInitialLoading,
      isForceFetchInProgress,
      error: isForceFetchInProgress ? null : error || null,
      isUserAuthorized,
      transactionsFound: isForceFetchInProgress ? true : transactionsFound,
      transactions:
        isForceFetchInProgress && transactions.length === 0 ? [] : transactions,
      cursor,
      refreshTransactions,
    }),
    [
      isLoading,
      effectiveInitialLoading,
      isForceFetchInProgress,
      error,
      isUserAuthorized,
      transactionsFound,
      transactions,
      cursor,
      refreshTransactions,
    ],
  );
  useEffect(() => {
    if (error) {
      if (!transactionsData || transactions.length === 0) {
        logError(error, `${PROVIDER_NAME} - Initial Load Error`);
      } else {
        logError(error, `${PROVIDER_NAME} - Refresh Error`);
      }
    } else if (isInitialLoading) {
      logInfo(
        'Loading in progress for transactions (may be waiting for wallets)',
        PROVIDER_NAME,
      );
    }
  }, [error, isInitialLoading, transactionsData, transactions.length]);
  return (
    <QueryClientProvider client={reactQueryClient}>
      <TransactionsContext.Provider value={contextValue}>
        {children}
      </TransactionsContext.Provider>
    </QueryClientProvider>
  );
};
